import { ServiceVariationSelectField } from "@/services/backend/omni/servicevariation/components/service-variation-select";
import { UUID } from "@bitory-gmbh/nidavellir";
import t from "@/lang/lang";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { TextInputField } from "@/shared/components/field/input-field";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { EditorInputField } from "@/shared/components/field/editor-input-field";
import { contractBaseDataTextVariables } from "../types/contract-text-variables";
import { PaymentTermsCard } from "@/services/backend/billing/components/payment-terms-card";
import { BillingInformationCard } from "@/services/backend/billing/components/billing-information-card";
import {
  PatchRequest,
  updateOmniContractBaseDataCache,
  useOmniContractBaseDataPatchMutation,
  useOmniContractBaseDataShowQuery,
} from "../api/base-data-api";
import {
  ContractBaseDataPatch,
  patchContractBaseData,
} from "../types/base-data";

export function ContractBaseDataFields({ id }: { id: UUID }) {
  const {
    data,
    error: dataError,
    isLoading,
  } = useOmniContractBaseDataShowQuery({ id });
  const request: PatchRequest = { id };
  const [set, { error }] = useOptimisticDebouncedMutation(
    request,
    updateOmniContractBaseDataCache,
    useOmniContractBaseDataPatchMutation(),
  );

  if (dataError) {
    return <RTKQueryErrorAlert error={dataError} />;
  }
  if (isLoading || !data) {
    return <CardContentSkeleton />;
  }

  const variables = contractBaseDataTextVariables(data.serviceKind);
  const patch = (p: ContractBaseDataPatch) =>
    set({ ...request, ...p }, patchContractBaseData(data, p));

  return (
    <div className="grid gap-4">
      {/* A rename to a taken label is a conflict, not a field error. */}
      <RTKQueryErrorAlert error={error} />
      <TextInputField
        label={t("Bezeichnung")}
        description={t(
          "Unterscheidet die Basisdaten eines Unternehmens voneinander.",
        )}
        text={data.label}
        onTextChange={(label) => patch({ label })}
        fieldName="label"
      />
      <ServiceVariationSelectField
        serviceKind={data.serviceKind}
        value={data.serviceVariation}
        onValueChange={(serviceVariation) => patch({ serviceVariation })}
        fieldName="serviceVariation"
        description={t(
          "Optional. Ein neuer Vertrag erhält das Leistungsverzeichnis dieser Variation, sonst das als aktiv markierte. Eine Variation ist je Unternehmen eindeutig.",
        )}
      />
      <TextInputField
        label={t("Titel")}
        text={data.title}
        onTextChange={(title) => patch({ title })}
        fieldName="title"
      />
      <TextInputField
        label={t("Untertitel")}
        text={data.subtitle}
        onTextChange={(subtitle) => patch({ subtitle })}
        fieldName="subtitle"
      />
      <EditorInputField
        // The editor highlights the variables it is mounted with.
        key={variables.map((v) => v.name).join(",")}
        label={t("Text")}
        initialHtml={data.text}
        onHtmlChange={(text: string) => patch({ text })}
        variables={variables}
        fieldName="text"
      />
      <div className="grid gap-2 xl:grid-cols-2">
        <PaymentTermsCard
          paymentTerms={data.paymentTerms}
          onPaymentTermsChange={(paymentTerms) => patch({ paymentTerms })}
          fieldName="paymentTerms"
        />
        <BillingInformationCard
          billingInformation={data.billingInformation}
          onBillingInformationChange={(billingInformation) =>
            patch({ billingInformation })
          }
          fieldName="billingInformation"
        />
      </div>
    </div>
  );
}
